import React from 'react';

import { EmptyIllustration } from '@dashboard/components/empty-states/EmptyIllustration';
import ButtonLink from '@dashboard/components/mui-atoms/ButtonLink';
import IconButtonLink from '@dashboard/components/mui-atoms/IconButtonLink';
import Header from '@dashboard/components/pages/QueryListPage/PageWrapper/Header';
import { createTableComponent } from '@dashboard/components/pages/QueryListPage/TableList/WpDataGrid';
import PrincipalTextCell from '@dashboard/components/table/cells/SimpleTextCell';
import { FWorkflowFragment, useWorkflowQuery, useWorkflowsQuery } from '@dashboard/generated/wp/platform';
import usePlatformNavigator from '@dashboard/hooks/navigate/usePlatformNavigator';
import { useWorkflowRunsListHref } from '@dashboard/hooks/route/hrefs';
import { HistoryOutlined } from '@mui/icons-material';
import { Chip, Link, Paper, Stack, Typography } from '@mui/material';
import useQueryDataTableProps from '@wp-react/hooks/useQueryDataTableProps';

import { DateFormatter, NumberFormatter } from '@waypoint/react-formatters';

import CreateWorkflowButton from './CreateWorkflowButton';

const WorkflowsTable = createTableComponent<FWorkflowFragment>({
  emptyResults: <EmptyIllustration caption="No workflows found." />,
  columns: [
    {
      headerName: 'Name',
      renderCell: ({ row }) => <PrincipalTextCell>{row.name}</PrincipalTextCell>,
    },
    {
      headerName: 'Runs',
      renderCell: function RunsCell({ row }) {
        const { data } = useWorkflowQuery({ variables: { id: row.id } });
        const workflow = data?.Workflow;
        if (!workflow) {
          return <></>;
        }
        return (
          <Typography color="text.primary">
            <NumberFormatter value={workflow.runsCount} />
          </Typography>
        );
      },
    },
    {
      headerName: 'Last ran',
      renderCell: function LastRunCell({ row }) {
        const { data } = useWorkflowQuery({ variables: { id: row.id } });
        const workflow = data?.Workflow;
        if (!workflow) {
          return <></>;
        }
        if (!workflow.lastRunAt) {
          return <>Never</>;
        }
        return (
          <Typography color="text.primary">
            <DateFormatter value={workflow.lastRunAt} />
          </Typography>
        );
      },
    },
    {
      headerName: 'Last edited',
      renderCell: function LastEditCell({ row }) {
        const { data } = useWorkflowQuery({ variables: { id: row.id } });
        const workflow = data?.Workflow;
        if (!workflow) {
          return <></>;
        }
        if (!workflow.lastRunAt) {
          return <>Never</>;
        }
        return (
          <Typography color="text.primary">
            <DateFormatter value={workflow.updatedAt || workflow.createdAt} />
          </Typography>
        );
      },
    },
  ],
});

export default function WorkflowsList() {
  const { data } = useWorkflowsQuery({ fetchPolicy: 'network-only' });
  const navigator = usePlatformNavigator();

  const workflowRunsListHref = useWorkflowRunsListHref();
  const { pagination } = useQueryDataTableProps(data?.Workflows);
  const nodes = data?.Workflows.nodes;
  return (
    <Paper>
      <Header
        pagination={pagination}
        title={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h4">Workflows</Typography>
            <Link target="_blank" href="https://www.usewaypoint.com/docs/workflow-basics">
              <Chip label="BETA" size="small" sx={{ cursor: 'pointer' }} />
            </Link>
          </Stack>
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <ButtonLink
              size="small"
              href={workflowRunsListHref}
              color="secondary"
              startIcon={<HistoryOutlined fontSize="small" />}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              Run history
            </ButtonLink>
            <IconButtonLink
              size="small"
              href={workflowRunsListHref}
              color="secondary"
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <HistoryOutlined fontSize="small" />
            </IconButtonLink>

            <CreateWorkflowButton />
          </Stack>
        }
      />
      <WorkflowsTable
        nodes={nodes}
        onRowClick={(row) => {
          navigator.showWorkflow(row.id);
        }}
      />
    </Paper>
  );
}
